import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { logger } from '@/utils/logger';

/**
 * `content_angles` — account-scoped (`table-scope.generated.ts`), written by
 * `brief-order-workflow.ts`. Ranked strategic angles ("Bold claim", "Social
 * proof"...) surfaced by the brief pipeline. Read-only here: the Studio Angle
 * chip lets a generation pick one, it never writes this table.
 */
export interface ContentAngle {
  id: string;
  lane: string;
  statement: string;
  rankScore: number | null;
  /** The audience this angle was researched for, when the run recorded one. */
  audienceId: string | null;
}

/**
 * Angles are written per brief run and the persist step is idempotent only
 * WITHIN a run, so four runs against the same audience leave four copies of the
 * same sentence in the table (prod, 2026-08-08). Nothing downstream dedupes
 * them, so the picker showed the same angle several times over. Collapse on the
 * normalised statement and keep the first — the list is ordered by rank, so the
 * survivor is the best-ranked copy.
 */
export function dedupeAngles(angles: ContentAngle[]): ContentAngle[] {
  const seen = new Set<string>();
  const kept: ContentAngle[] = [];
  for (const angle of angles) {
    const key = angle.statement
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    kept.push(angle);
  }
  return kept;
}

async function fetchContentAngles(ownerId: string): Promise<ContentAngle[]> {
  const { data, error } = await supabase
    .from('content_angles')
    .select('id, lane, statement, rank_score, audience_id')
    .eq('user_id', ownerId)
    .neq('status', 'retired')
    .order('rank_score', { ascending: false })
    // Read wider than the picker shows: duplicates across brief runs are
    // collapsed below, and the list is then narrowed again by audience.
    .limit(60);

  if (error) {
    logger.error('Error fetching content angles:', error);
    throw error;
  }

  return dedupeAngles(
    (data ?? []).map((row) => ({
      id: row.id,
      lane: row.lane,
      statement: row.statement,
      rankScore: row.rank_score,
      audienceId: row.audience_id,
    })),
  );
}

export function useContentAngles() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['content-angles', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const ownerId = await resolveOwnerId(user.id);
      return fetchContentAngles(ownerId);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  return {
    angles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * One angle by id, for a Concept whose `angle_id` is missing from the picker list.
 *
 * `useContentAngles` is a 60-row, statement-deduplicated, 5-minute-cached view.
 * An angle written by a run that finished a minute ago is absent from that cache,
 * and an angle whose statement matches a better-ranked copy is collapsed onto the
 * other copy's id. Either way the Concept's own angle was labelled "From an
 * earlier run" although this run wrote it (ITC run 4eda4aae, 2026-10-01). The id
 * lookup is exact, so the row can say what the angle really is.
 */
export function useContentAngleById(angleId: string | null | undefined, enabled = true) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['content-angle', user?.id, angleId],
    queryFn: async (): Promise<(ContentAngle & { retired: boolean }) | null> => {
      if (!user || !angleId) return null;
      const ownerId = await resolveOwnerId(user.id);
      const { data, error } = await supabase
        .from('content_angles')
        .select('id, lane, statement, rank_score, audience_id, status')
        .eq('id', angleId)
        .eq('user_id', ownerId)
        .maybeSingle();
      if (error) {
        logger.error('Error fetching content angle:', error);
        throw error;
      }
      if (!data) return null;
      return {
        id: data.id,
        lane: data.lane,
        statement: data.statement,
        rankScore: data.rank_score,
        audienceId: data.audience_id,
        retired: data.status === 'retired',
      };
    },
    enabled: enabled && !!user?.id && !!angleId,
    staleTime: 60 * 1000,
  });

  return { angle: query.data ?? null, isLoading: query.isLoading };
}
